// notification.service.ts
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * @service NotificationUpdateService
 * @description Service responsible for broadcasting notification update events across the application.
 *
 * This service implements a simple Publish-Subscribe (Pub/Sub) pattern using RxJS.
 * It is designed to decouple the components that generate notification events (producers)
 * from the components that need to react to them (consumers), such as the notification bell icon
 * in the header or the notification list view.
 *
 * **Usage Scenario:**
 * 1. **Producer**: When a new notification arrives (e.g., via WebSocket or a background polling service) or when a user marks a notification as read, the producer calls `notifyUpdate()`.
 * 2. **Consumer**: The HeaderComponent or NotificationListComponent subscribes to `updateNotifications$`. When an emission occurs, they refresh their data from the backend.
 *
 * **Key Responsibilities:**
 * 1. **Event Bus**: Acts as a central event bus specifically for notification-related state changes.
 * 2. **State Propagation**: Ensures that UI components stay in sync with the latest notification status without direct coupling.
 *
 * @feature 1. Real-time UI updates for notifications.
 * @feature 2. Decoupled communication between unrelated components.
 *
 * @architecture
 * Uses a `private Subject` to handle the event emission and exposes it as a `public Observable`.
 * This ensures that only this service can trigger the event (via `notifyUpdate`), while external consumers can only listen.
 *
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root',
})

export class NotificationUpdateService {
  /**
   * @private
   * @type {Subject<void>}
   * @description The internal RxJS Subject used to multicast the update event.
   * It emits `void` as the event itself is the signal, and no payload is currently required.
   */
  private updateNotificationsSubject = new Subject<void>();

  // Observable to subscribe for notifications
  /**
   * @public
   * @type {Observable<void>}
   * @description Public observable stream that components can subscribe to.
   * Emits a value whenever `notifyUpdate()` is called.
   *
   * @example
   * // In a component's ngOnInit
   * this.notificationUpdateService.updateNotifications$.subscribe(() => {
   *   this.refreshNotificationCount();
   * });
   */
  updateNotifications$ = this.updateNotificationsSubject.asObservable();

  // Method to trigger a notification update
  /**
   * @method notifyUpdate
   * @description Triggers a notification update event.
   *
   * Calling this method causes the `updateNotificationsSubject` to emit a value,
   * which notifies all active subscribers of `updateNotifications$`.
   * This is typically called after an action that modifies the state of notifications
   * (e.g., receiving a new one, deleting one, marking as read).
   *
   * @returns {void}
   * @side-effect Emits a value to the `updateNotifications$` stream.
   *
   * @example
   * // After successfully marking a notification as read
   * this.notificationUpdateService.notifyUpdate();
   */
  notifyUpdate() {
    this.updateNotificationsSubject.next();
  }
}
